import { Suspense, lazy, ElementType, ReactNode } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import PasswordChangeGuard from '../guards/PasswordChangeGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
import { PATH_DASHBOARD } from './paths';
// components
import LoadingScreen from '../components/LoadingScreen';
import { InstructorsList } from 'src/pages/dashboard/instructors';
import InstructorDetail from 'src/sections/dashboard/instructors/InstructorDetail';
import BlogList from 'src/sections/dashboard/blogs/components/BlogList';
import SuccessStoriesList from 'src/sections/dashboard/successStories/components/SuccessStoriesList';
import CompanyProfileContent from 'src/sections/dashboard/company-profile';
import EventsList from 'src/sections/dashboard/events/components/EventsList';
import ProgramBatches from 'src/pages/dashboard/ProgramBatches';
import ProgramQuizAnalysis from 'src/pages/dashboard/ProgramQuizAnalysis';
import ProgramBatchReport from 'src/pages/dashboard/ProgramBatchReport';
import SeoPageList from 'src/sections/dashboard/seoPages/components/SeoPageList';
import HomeStatsList from 'src/sections/dashboard/home-stats';
import AboutPageEditor from 'src/sections/dashboard/about-page';
import NotificationsList from 'src/sections/dashboard/notifications';
import { UserList } from 'src/pages/dashboard/users';
import PackageList from 'src/sections/dashboard/packages';
import { TrackList } from 'src/pages/dashboard/tracks';
import { CategoryList } from 'src/sections/dashboard/categories';
import { DiplomaList } from 'src/sections/dashboard/diplomas';
import CourseList from 'src/sections/dashboard/courses/components/CourseList';
import CourseItem from 'src/sections/dashboard/courses/course-view/CourseView';
import CourseReport from 'src/sections/dashboard/courses/course-report/CourseReport';
import DiplomaReport from 'src/sections/dashboard/diplomas/diploma-report/DiplomaReport';
import { Messages } from 'src/pages/Messages';
import { PromoCode } from 'src/pages/dashboard/prmoCode';
import CustomLoggersItems from 'src/sections/dashboard/customLoggers';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

// Phase 7.12: the legacy /dashboard data pages below were AuthGuard-only —
// a SUPPORT (or any non-admin) account could navigate to them and render
// broken/empty admin pages even though the BE refuses the API calls. Gate the
// admin data surface; hasContent shows the friendly "permission denied" panel
// instead of an empty layout shell.
//
// Every guard below mirrors a BE @Auth list. super-admin always passes
// (inheritance lives inside RoleBasedGuard), so it is omitted from the arrays.
// These guards are convenience, not security — the BE is the real boundary.

// Learning catalogue: courses, tracks, paths, packages, diplomas, batches.
// customer-care keeps admin-like access here. DELETE stays admin-only (BE).
function RequireLearning({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Packages is the one Learning page sales-manager may open (view-only pricing).
// It gets its own guard so sales-manager is NOT let into the rest of the
// catalogue (courses/tracks/diplomas). Write actions are hidden in the page.
function RequirePackages({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'sales-manager']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Marketing content: blogs, success stories, SEO pages, home stats, about
// page, notifications. Programs are NOT here — they are catalogue-adjacent and
// sales is deliberately kept out of them.
function RequireContent({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'sales']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Diploma landing pages (SPEC §D.1): the marketing roles edit the page content
// of a diploma without reaching the catalogue (price, batches stay in the core
// modal behind RequireLearning). Mirrors the BE landing roles.
function RequireLanding({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'sales', 'sales-manager']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Tracked lead forms (lead-forms SPEC §D.1, §H): the builder (list + editor).
// Mirrors BE LEAD_FORM_ROLES (admin, super-admin, sales-manager, marketing);
// sales and customer-care get no access (Q1).
function RequireLeadForms({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'sales-manager', 'marketing']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// The leads inbox (lead-forms SPEC §D.8, §H). Mirrors BE LEAD_INBOX_ROLES
// (admin, super-admin, sales-manager, marketing; super-admin passes the guard
// on its own). Q1: adding sales is one line here, in LEAD_INBOX_ROLES and in
// useLeadFormRoles.
function RequireLeadInbox({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'sales-manager', 'marketing']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Programs (events) — customer-care owns these outright now, including the
// quiz analysis and per-batch reports hanging off them.
function RequirePrograms({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard
      roles={['admin', 'customer-care', 'instructor', 'coordinator']}
      hasContent
    >
      {children}
    </RoleBasedGuard>
  );
}

function RequireInstructors({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'sales']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

function RequireMessages({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'sales', 'support']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

function RequireAdmin({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// The "Students" surface (list + per-student drill-down) is the widest shared
// page: every scoped staff role reads it. What each role may DO on it is
// enforced per-action inside the page (and by the BE), not by this guard.
function RequireStudents({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard
      roles={['admin', 'customer-care', 'sales', 'sales-manager', 'support']}
      hasContent
    >
      {children}
    </RoleBasedGuard>
  );
}

// Payments and revenue moved off `sales` onto the new `sales-manager` role.
function RequirePayments({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'sales-manager']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Segments + expiring subscriptions stay with sales, and sales-manager sees
// them too.
function RequireSegments({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'sales', 'sales-manager']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

function RequirePromo({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'sales', 'sales-manager']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// EPIC E7 class reminders: the delivery ledger + the once-ever resend. Mirrors
// the BE @Auth on GET /v1/reminders/admin/* exactly — coordinator owns the
// timetable and must be able to see whether it reached anyone. `sales` is
// deliberately excluded, and the resend action inside the page is narrowed
// again to admin / customer-care because the BE narrows it too.
function RequireReminders({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'coordinator']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// PARTNER PLATFORM S1 — the whole partner surface behind ONE guard, because
// the backend puts it behind ONE @Auth list. Read off the controllers:
//
//   GET /v1/partner-applications        @Auth(ADMIN, SUPER_ADMIN)
//   GET /v1/partner-applications/:id    @Auth(ADMIN, SUPER_ADMIN)
//   GET /v1/admin/partners              @Auth(ADMIN, SUPER_ADMIN)
//   GET /v1/admin/partners/:id          @Auth(ADMIN, SUPER_ADMIN)
//   /v1/admin/partner-agreements/*      @Auth(ADMIN, SUPER_ADMIN)
//
// CUSTOMER-CARE IS NOT ON ANY OF THEM. An earlier draft admitted customer-care
// to the two lists on the belief that their @Auth included it; it does not, so
// every load answered a permission error. `super-admin` is not listed here
// because RoleBasedGuard grants it inherently — adding it would be a second,
// drifting copy of that rule.
//
// The two SUPER_ADMIN-only powers on this surface — flipping the public intake
// and breaking another reviewer's claim — are NOT routes. They are controls
// inside these pages, and they are ABSENT rather than disabled for anybody else
// (see hooks/usePartnerOps.ts).
function RequirePartnerOps({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// EPIC E1 Zoom hosts: Batch Management now carries the Zoom host tab, and
// picking the licence a batch runs on is the coordinator's job — they own the
// timetable that creates the clash in the first place. The rest of the batch
// surface (sessions, attendance, progress, materials) was already theirs to
// read on the Programs side, so this widens the guard rather than adding a
// second route. Zoom ACCOUNTS stay super-admin only; this is assignment, not
// credentials.
function RequireBatchOps({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'coordinator']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Student alerts: at-risk triage, shared by customer-care and support.
function RequireAlerts({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'customer-care', 'support']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// /dashboard/app is the KPI dashboard — signups, revenue, subscriptions. Only
// roles that can actually load those stats get it; for everyone else it used to
// render a wall of failed requests.
function RequireDashboard({ children }: { children: ReactNode }) {
  return (
    <RoleBasedGuard roles={['admin', 'sales-manager']} hasContent>
      {children}
    </RoleBasedGuard>
  );
}

// Phase 7.17: role-based landing — send each role to its FIRST permitted page
// so nobody lands on a Permission Denied panel. Order matters: the most
// privileged role a user holds wins.
function RoleLanding() {
  const { user } = useAuth();
  const roles: string[] = ((user as any)?.roles as string[]) || [];
  let to: string = PATH_AFTER_LOGIN;
  if (roles.includes('super-admin')) to = PATH_DASHBOARD.settings.admins;
  else if (roles.includes('admin')) to = PATH_DASHBOARD.user.list;
  // marketing builds the lead forms and works their leads; it has no other page.
  else if (roles.includes('marketing')) to = PATH_DASHBOARD.leadForms;
  // Sales work the diploma landing pages and their leads (waitlist people are
  // promised a WhatsApp message), so both sales roles land on that list. The
  // sales-manager KPI dashboard stays one click away in the menu.
  else if (roles.includes('sales-manager')) to = PATH_DASHBOARD.landingPages;
  else if (roles.includes('sales')) to = PATH_DASHBOARD.landingPages;
  // Everyone else shares the Students surface as their primary page. support
  // no longer lands on Impersonate — that capability was removed from it.
  else if (roles.includes('customer-care')) to = PATH_DASHBOARD.user.list;
  else if (roles.includes('support')) to = PATH_DASHBOARD.user.list;
  // instructor only sees Programs, so land them straight there (checked last so
  // any broader role a mixed account might hold wins the landing page).
  else if (roles.includes('instructor')) to = PATH_DASHBOARD.events;
  // coordinator owns the live-session timetable, which lives under Programs.
  // Without this they fall through to PATH_AFTER_LOGIN and hit a page their
  // role cannot read.
  else if (roles.includes('coordinator')) to = PATH_DASHBOARD.events;
  return <Navigate to={to} replace />;
}

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        // Staff sign-in: a new account chooses its own password before anything else.
        {
          path: 'change-password',
          element: (
            <PasswordChangeGuard>
              <ChangePassword />
            </PasswordChangeGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
      ],
    },

    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <RoleLanding />, index: true },
        { path: 'app', element: <RequireDashboard><GeneralApp /></RequireDashboard> },
        { path: 'instructors', element: <RequireInstructors><InstructorsList /></RequireInstructors> },
        { path: 'instructors/:id', element: <RequireInstructors><InstructorDetail /></RequireInstructors> },
        { path: 'messages', element: <RequireMessages><Messages /></RequireMessages> },
        { path: 'packages', element: <RequirePackages><PackageList /></RequirePackages> },
        { path: 'promo-code', element: <RequirePromo><PromoCode /></RequirePromo> },
        { path: 'courses', element: <RequireLearning><CourseList /></RequireLearning> },
        { path: 'diplomas', element: <RequireLearning><DiplomaList /></RequireLearning> },
        { path: 'diplomas/:id/report', element: <RequireLearning><DiplomaReport /></RequireLearning> },
        { path: 'diplomas/:id/landing', element: <RequireLanding><DiplomaLandingEditorPage /></RequireLanding> },
        { path: 'landing-pages', element: <RequireLanding><LandingPagesList /></RequireLanding> },
        { path: 'lead-forms', element: <RequireLeadForms><LeadFormsListPage /></RequireLeadForms> },
        // SPEC-S.8.2: static, so it outranks lead-forms/:id (react-router 6 ranks static segments first).
        { path: 'lead-forms/google-sheet', element: <RequireLeadForms><LeadSheetPage /></RequireLeadForms> },
        { path: 'lead-forms/:id', element: <RequireLeadForms><LeadFormEditorPage /></RequireLeadForms> },
        // SPEC-T.8.4: the Google sign-in lands here (posts the result to the other tabs, closes the popup).
        { path: 'google-connected', element: <RequireLeadForms><GoogleConnectedPage /></RequireLeadForms> },
        { path: 'leads', element: <RequireLeadInbox><LeadsInboxPage /></RequireLeadInbox> },
        {
          path: 'batch-management',
          element: (
            <RequireBatchOps>
              <BatchesList />
            </RequireBatchOps>
          ),
        },
        {
          path: 'diplomas/:id/batches/:batchName',
          element: (
            <RequireBatchOps>
              <BatchManagement />
            </RequireBatchOps>
          ),
        },
        { path: 'courses/:id', element: <RequireLearning><CourseItem /></RequireLearning> },
        { path: 'courses/:id/report', element: <RequireLearning><CourseReport /></RequireLearning> },
        { path: 'screens', element: <RequireAdmin><CustomLoggersItems /></RequireAdmin> },
        { path: 'certificates', element: <RequireAdmin><Certificates /></RequireAdmin> },
        { path: 'payments', element: <RequirePayments><Payments /></RequirePayments> },
        { path: 'quiz-results', element: <RequireAdmin><QuizResults /></RequireAdmin> },
        {
          path: 'student-alerts',
          element: (
            <RequireAlerts>
              <StudentAlerts />
            </RequireAlerts>
          ),
        },
        { path: 'instructor-ratings', element: <RequireAdmin><InstructorRatings /></RequireAdmin> },
        { path: 'final-projects', element: <RequireAdmin><FinalProjects /></RequireAdmin> },
        { path: 'user-segments', element: <RequireSegments><UserSegments /></RequireSegments> },
        { path: 'reports', element: <RequireAdmin><Reports /></RequireAdmin> },
        { path: 'blog', element: <RequireContent><BlogList /></RequireContent> },
        { path: 'success-stories', element: <RequireContent><SuccessStoriesList /></RequireContent> },
        { path: 'company-profile', element: <RequireContent><CompanyProfileContent /></RequireContent> },
        { path: 'events', element: <RequirePrograms><EventsList /></RequirePrograms> },
        {
          // Static segment — declared before the ':eventId' routes below so it is
          // never captured as an event id.
          path: 'events/quiz-analysis',
          element: <RequirePrograms><ProgramQuizAnalysis /></RequirePrograms>,
        },
        {
          path: 'events/:eventId/batches',
          element: <RequirePrograms><ProgramBatches /></RequirePrograms>,
        },
        {
          path: 'events/:eventId/batches/:batchId',
          element: <RequirePrograms><ProgramBatchReport /></RequirePrograms>,
        },
        { path: 'seoPages', element: <RequireContent><SeoPageList /></RequireContent> },
        { path: 'home-stats', element: <RequireContent><HomeStatsList /></RequireContent> },
        { path: 'about-page', element: <RequireContent><AboutPageEditor /></RequireContent> },
        { path: 'notifications', element: <RequireContent><NotificationsList /></RequireContent> },
        { path: 'reminders', element: <RequireReminders><Reminders /></RequireReminders> },
        // PARTNER PLATFORM S1 — the review queue, the partners list and the
        // tabbed partner page.
        //
        // `applications` is a STATIC segment declared before the ':id' sibling
        // so /dashboard/partners/applications can never resolve as a partner
        // whose id is the literal string "applications". React Router v6 ranks
        // static above dynamic on its own, but the order is written out anyway
        // because the BE's own route table depends on exactly the same rule and
        // the two must be read together.
        //
        // ONE GUARD ACROSS ALL THREE, because the API puts all three behind
        // one @Auth(ADMIN, SUPER_ADMIN) list. A narrower guard on the detail
        // pages would imply a boundary the backend does not enforce.
        //
        // PARTNER PLATFORM S2 adds `courses` and `courses/:courseId` as a
        // THIRD static branch under the same root, declared BEFORE ':id' for
        // exactly the same reason `applications` is: otherwise
        // /dashboard/partners/courses resolves as a partner whose id is the
        // literal string "courses", and the backend's own route table depends
        // on the identical rule (`GET /revisions` before `GET /:courseId`).
        //
        // THE SAME GUARD AGAIN. Read off §12.B: the queue, the review page,
        // approve, reject, unpublish, republish, the revisions and the media
        // mint are all @Auth(ADMIN, SUPER_ADMIN). The two SUPER_ADMIN-only
        // powers on this surface — transferring a course back to Kayfa and
        // editing the studio's course defaults — are NOT routes; they are
        // controls inside these pages, ABSENT rather than disabled for anybody
        // else (hooks/usePartnerOps.ts).
        {
          path: 'partners',
          children: [
            { element: <RequirePartnerOps><Partners /></RequirePartnerOps>, index: true },
            {
              path: 'applications',
              element: (
                <RequirePartnerOps>
                  <PartnerApplications />
                </RequirePartnerOps>
              ),
            },
            {
              path: 'courses',
              children: [
                {
                  element: (
                    <RequirePartnerOps>
                      <PartnerCourses />
                    </RequirePartnerOps>
                  ),
                  index: true,
                },
                {
                  path: ':courseId',
                  element: (
                    <RequirePartnerOps>
                      <PartnerCourseDetail />
                    </RequirePartnerOps>
                  ),
                },
              ],
            },
            // BUNNY EPIC — the partner video operations screen. A FOURTH
            // literal segment, and it MUST stay above `:id` for the same reason
            // `applications` and `courses` do: /dashboard/partners/bunny would
            // otherwise resolve as a partner whose id is the string "bunny".
            //
            // The same guard again. Read off §9.D: the status, the list, the
            // detail, refresh, re-encode, archive, unarchive and detach are all
            // @Auth(ADMIN, SUPER_ADMIN). Purge is SUPER_ADMIN-only and is a
            // control inside the page rather than a route of its own.
            {
              path: 'bunny',
              element: (
                <RequirePartnerOps>
                  <PartnerBunny />
                </RequirePartnerOps>
              ),
            },
            {
              path: ':id',
              element: (
                <RequirePartnerOps>
                  <PartnerDetail />
                </RequirePartnerOps>
              ),
            },
          ],
        },
        {
          path: 'category',
          element: (
            <RequireLearning>
              <CategoryList />
            </RequireLearning>
          ),
        },
        {
          path: 'tracks',
          element: (
            <RequireLearning>
              <TrackList />
            </RequireLearning>
          ),
        },

        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/list" replace />, index: true },
            { path: 'list', element: <RequireStudents><UserList /></RequireStudents> },

            { path: 'account/:id', element: <RequireStudents><UserAccount /></RequireStudents> },
          ],
        },
        // Phase 2.4: admin self-service pages.
        {
          path: 'account',
          children: [
            { element: <Navigate to="/dashboard/account/security" replace />, index: true },
            { path: 'security', element: <Security /> },
            { path: 'sessions', element: <Sessions /> },
          ],
        },
        // Phase 3.5: super-admin only management.
        {
          path: 'settings',
          children: [
            { element: <Navigate to="/dashboard/settings/admins" replace />, index: true },
            {
              path: 'admins',
              element: (
                <RoleBasedGuard roles={['super-admin']} hasContent>
                  <Admins />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'admins/:id/sessions',
              element: (
                <RoleBasedGuard roles={['super-admin']} hasContent>
                  <AdminSessions />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'audit',
              element: (
                <RoleBasedGuard roles={['super-admin']} hasContent>
                  <AuditLog />
                </RoleBasedGuard>
              ),
            },
            {
              // E1: connecting a Zoom account means typing a live client
              // secret, so this stays with the role that already owns admin
              // credentials. Coordinators assign the imported hosts from Batch
              // Management and never see this page.
              path: 'zoom',
              element: (
                <RoleBasedGuard roles={['super-admin']} hasContent>
                  <ZoomAccounts />
                </RoleBasedGuard>
              ),
            },
            {
              // E6: the recording pipeline's health report. WIDER THAN THE REST
              // OF THIS GROUP on purpose — it is read-only, no credential is
              // entered on it, and the role that publishes and retries
              // recordings every day is `admin`, not `super-admin`. The one
              // destructive action in the epic (destroying a stored file) lives
              // on the cohort's own tab behind its own super-admin gate and is
              // deliberately not reachable from here.
              path: 'recordings',
              element: (
                <RoleBasedGuard roles={['admin', 'super-admin']} hasContent>
                  <RecordingsSettings />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        // Phase 6.8: customer-care impersonation surface.
        {
          path: 'support',
          children: [
            {
              element: <Navigate to="/dashboard/support/impersonate" replace />,
              index: true,
            },
            {
              path: 'impersonate',
              element: (
                <RoleBasedGuard roles={['customer-care']} hasContent>
                  <Impersonate />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'active',
              element: (
                <RoleBasedGuard roles={['super-admin']} hasContent>
                  <ActiveImpersonations />
                </RoleBasedGuard>
              ),
            },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      // Hitting the bare origin must go through the SAME role landing as
      // /dashboard. Rendering GeneralApp here directly bypassed
      // RequireDashboard, so a support/sales/customer-care user who typed the
      // domain got a revenue dashboard whose every KPI call 403s to zero.
      children: [{ element: <RoleLanding />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ChangePassword = Loadable(lazy(() => import('../pages/auth/ChangePassword')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
// B.4 follow-up: admin certificates page
const Certificates = Loadable(lazy(() => import('../pages/dashboard/Certificates')));
// Payments admin list (no-admin-payments-endpoint FE)
const Payments = Loadable(lazy(() => import('../pages/dashboard/Payments')));
// Phase 7.2: quiz attempt results
const QuizResults = Loadable(lazy(() => import('../pages/dashboard/QuizResults')));
// Phase 8.x: smart alerts / at-risk students
const StudentAlerts = Loadable(lazy(() => import('../pages/dashboard/StudentAlerts')));
// Phase 8.x: instructor ratings
const InstructorRatings = Loadable(lazy(() => import('../pages/dashboard/InstructorRatings')));
const FinalProjects = Loadable(lazy(() => import('../pages/dashboard/FinalProjects')));
const UserSegments = Loadable(lazy(() => import('../pages/dashboard/UserSegments')));
// EPIC E7: class-reminder delivery ledger + per-delivery resend.
const Reminders = Loadable(lazy(() => import('../pages/dashboard/Reminders')));
const Reports = Loadable(lazy(() => import('../pages/dashboard/Reports')));
// PARTNER PLATFORM S1: the review queue, the partners list and the partner page
// (profile / agreements / ledger). Lazily loaded like every other dashboard
// page, so the roles that never open them never download them.
const PartnerApplications = Loadable(
  lazy(() => import('../pages/dashboard/partners/Applications')),
);
const Partners = Loadable(lazy(() => import('../pages/dashboard/partners/Partners')));
// Diploma landing pages: the per-diploma editor and the list the sales roles
// reach it from (they cannot open the Diplomas list).
const DiplomaLandingEditorPage = Loadable(
  lazy(() => import('../pages/dashboard/DiplomaLandingEditorPage')),
);
const LandingPagesList = Loadable(
  lazy(() => import('../sections/dashboard/diplomas/landing/LandingPagesList')),
);
// Tracked lead forms: the builder list and one form's editor (remounted per id).
const LeadFormsListPage = Loadable(lazy(() => import('../pages/dashboard/LeadFormsListPage')));
const LeadFormEditorPage = Loadable(lazy(() => import('../pages/dashboard/LeadFormEditorPage')));
const LeadSheetPage = Loadable(lazy(() => import('../pages/dashboard/LeadSheetPage')));
const GoogleConnectedPage = Loadable(lazy(() => import('../pages/dashboard/GoogleConnectedPage')));
// The leads inbox of every tracked lead form.
const LeadsInboxPage = Loadable(lazy(() => import('../pages/dashboard/LeadsInboxPage')));
const PartnerDetail = Loadable(
  lazy(() => import('../pages/dashboard/partners/PartnerDetail')),
);
// PARTNER PLATFORM S2: the course review queue and the course review page.
const PartnerCourses = Loadable(
  lazy(() => import('../pages/dashboard/partners/PartnerCourses')),
);
const PartnerCourseDetail = Loadable(
  lazy(() => import('../pages/dashboard/partners/PartnerCourseDetail')),
);
// BUNNY EPIC: the partner video operations screen. Same @Auth list as the rest
// of the partner surface for the eight endpoints it reads and writes; the one
// SUPER_ADMIN-only power on it (destroying a zero-byte orphan at the provider)
// is not a route but a control inside the page, ABSENT rather than disabled for
// anybody else (hooks/usePartnerOps.ts).
const PartnerBunny = Loadable(
  lazy(() => import('../pages/dashboard/partners/PartnerBunny')),
);
// Batch Management — diploma-batch class sessions, attendance, and completion score.
const BatchesList = Loadable(
  lazy(() => import('../sections/dashboard/diplomas/batch-management/BatchesList')),
);
const BatchManagement = Loadable(
  lazy(() => import('../sections/dashboard/diplomas/batch-management/BatchManagement')),
);

// USER
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));

// ADMIN ACCOUNT (Phase 2.4)
const Security = Loadable(lazy(() => import('../pages/dashboard/account/Security')));
const Sessions = Loadable(lazy(() => import('../pages/dashboard/account/Sessions')));

// SETTINGS — super-admin only (Phase 3.5)
const Admins = Loadable(lazy(() => import('../pages/dashboard/settings/Admins')));
const AdminSessions = Loadable(
  lazy(() => import('../pages/dashboard/settings/AdminSessions')),
);
const AuditLog = Loadable(
  lazy(() => import('../pages/dashboard/settings/AuditLog')),
);
// EPIC E1: Zoom accounts & host pool.
// EPIC E6: the recording pipeline's standing health report.
const RecordingsSettings = Loadable(
  lazy(() => import('../pages/dashboard/settings/Recordings')),
);
const ZoomAccounts = Loadable(
  lazy(() => import('../pages/dashboard/settings/ZoomAccounts')),
);

// SUPPORT — customer-care impersonation surface (Phase 6.8)
const Impersonate = Loadable(
  lazy(() => import('../pages/dashboard/support/Impersonate')),
);
const ActiveImpersonations = Loadable(
  lazy(() => import('../pages/dashboard/support/ActiveImpersonations')),
);

// TEST RENDER PAGE BY ROLE

// MAIN

const Page404 = Loadable(lazy(() => import('../pages/Page404')));
